import { useEffect, useState } from "react";
import { unwrap } from "../utils/queryFn";
import Assignation from "../models/Assignation";
import AssignationService from "../services/AssignationService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import RoleUtils from "../utils/RoleUtils";

interface UseAssignationFormParams {
  externalId: string;
  onClose: (updated: boolean) => void;
}

const useAssignationForm = ({ externalId, onClose }: UseAssignationFormParams) => {
  const [isLoading, setIsLoading] = useState(false);
  const [instance, setInstance] = useState<Assignation | undefined>();
  const [assignationStartDate, setAssignationStartDate] = useState<string>("");
  const [assignationEndDate, setAssignationEndDate] = useState<string | undefined>();
  const [showDialog, setShowDialog] = useState(true);
  const [canSave, setCanSave] = useState(true);

  useEffect(() => {
    fetchAssignation(externalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAssignation = async (externalId: string) => {
    setIsLoading(true);
    try {
      const assignation = await unwrap(AssignationService.getByExternalId(externalId));
      setInstance(assignation);
      setAssignationStartDate(new Date(assignation.shortStartDate).getTime().toString());
    } catch (error) {
      NotificationService.error("Error getting assignation");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateAssignation = async (assignation: Assignation) => {
    if (!RoleUtils.isAdminOrManager()) {
      NotificationService.error("You don't have permission to do this action");
      onClose(false);
      return;
    }

    try {
      await unwrap(AssignationService.update(assignation));
      NotificationService.info("Updated successfully");
      onClose(true);
    } catch (error) {
      NotificationService.error(error instanceof Error ? error.message : "Error updating assignation");
      onClose(false);
    }
  };

  const validateDates = async (startDate: string, endDate?: string) => {
    if (!instance) return;
    try {
      const isValid = await unwrap(
        AssignationService.validateAssignationStartDate(instance.position.externalId!, startDate, endDate || ""),
      );
      setCanSave(isValid);
    } catch {
      setCanSave(false);
    }
  };

  const handleUpdateStartDate = (startDate: number) => {
    const value = startDate.toString();
    setAssignationStartDate(value);
    validateDates(value, assignationEndDate);
  };

  const handleUpdateEndDate = (endDate?: number) => {
    const value = endDate?.toString();
    setAssignationEndDate(value);
    validateDates(assignationStartDate, value);
  };

  const handleOnCancel = () => {
    setShowDialog(false);
    onClose(false);
  };

  return {
    isLoading,
    instance,
    showDialog,
    canSave,
    handleUpdateAssignation,
    handleUpdateStartDate,
    handleUpdateEndDate,
    handleOnCancel,
  };
};

export default useAssignationForm;
